import {
  ArchiveIcon,
  ClockClockwiseIcon,
  DotsThreeVerticalIcon,
} from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ItemStatusEnum,
  useApiMutation,
  useTableDataQuery,
} from '@/modules/common';
import {
  ChangeStatusDialog,
  PaginatedTable,
  TableItemStatus,
} from '@/modules/common/components';

import { OverlayItem } from '../types';

interface OverlaysTableProps {
  onClick: (item: OverlayItem) => void;
}

export function OverlaysTable({ onClick }: OverlaysTableProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState<OverlayItem | null>(null);
  const isArchived = activeOverlay?.status === ItemStatusEnum.Archived;

  const { data, isFetching, refetch, ...tableProps } =
    useTableDataQuery<OverlayItem>({
      url: '/v2/overlays',
      persistState: true,
    });

  const updateOverlayMutation = useApiMutation<OverlayItem>(
    '/overlays',
    {
      method: 'PATCH',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const onArchiveRestoreClick = (
    event: React.SyntheticEvent<HTMLDivElement>,
    maskRow: OverlayItem
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveOverlay(maskRow);
  };

  const toggleModal = (nextOpenState: boolean) => {
    if (!nextOpenState) {
      setActiveOverlay(null);
    }
  };

  const toggleStatusOverlay = async () => {
    if (activeOverlay?.id) {
      setIsUpdating(true);
      try {
        await updateOverlayMutation.mutateAsync({
          ...activeOverlay,
          status:
            activeOverlay.status === ItemStatusEnum.Archived
              ? ItemStatusEnum.Ready
              : ItemStatusEnum.Archived,
        });
        await refetch();
        setActiveOverlay(null);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const columns: ColumnDef<OverlayItem>[] = [
    {
      accessorKey: 'name',
      header: 'Overlay Name',
      cell: ({ row }) => (
        <span className="line-clamp-1">{row.getValue('name')}</span>
      ),
    },
    {
      accessorKey: 'imagesPublicUrls',
      header: 'Preview',
      cell: ({ row }) => {
        return (
          <img
            src={row.original.imagePublicUrl}
            alt="Overlay preview image"
            className="w-auto h-auto max-w-[36px] max-h-[36px] object-cover border border-zinc-200"
          />
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Upload Date',
      cell: ({ row }) =>
        format(new Date(row.getValue('updatedAt')), 'yyyy-MM-dd'),
    },
    {
      size: 40,
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const isArchived = row.original.status === ItemStatusEnum.Archived;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                data-testid="overlays-table-actions"
                className="h-8 w-8 p-0"
              >
                <span className="sr-only">Open menu</span>
                <DotsThreeVerticalIcon color={colors.zinc[400]} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid={isArchived ? 'restore' : 'archive'}
                className={cn(
                  !isArchived && 'focus:text-destructive text-destructive'
                )}
                onClick={(event) => onArchiveRestoreClick(event, row.original)}
              >
                {isArchived ? (
                  <ClockClockwiseIcon />
                ) : (
                  <ArchiveIcon color="hsl(var(--destructive))" />
                )}
                {isArchived ? 'Restore' : 'Archive'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <PaginatedTable<OverlayItem>
        data={data}
        onClick={onClick}
        columns={columns}
        searchable
        searchPlaceholder="Search by overlay name"
        emptyDescription="No overlays uploaded yet"
        isLoading={isFetching}
        {...tableProps}
      />

      <ChangeStatusDialog
        onOpenChange={toggleModal}
        isArchived={isArchived}
        isUpdating={isUpdating}
        onSubmit={toggleStatusOverlay}
        isOpen={Boolean(activeOverlay?.id)}
        title={`${isArchived ? 'Restore' : 'Archive'} overlay “${activeOverlay?.name}”?`}
        restoreDescription="Are you sure you want to restore this overlay?"
        archiveDescription="Are you sure you want to archive this overlay? You can restore it later if needed."
      />
    </>
  );
}
